/**
 * Application Entry Point
 *
 * Bootstraps the Vue app using dynamic imports inside an async IIFE.
 * The initialization order is critical for the auth flow:
 *
 *  1. Pinia — store must exist before any composable runs
 *  2. vue-i18n — must be registered before any component that calls useLocale()
 *  3. hydrate() — calls `store.markLoading()` synchronously (before any await),
 *     ensuring the auth guard sees `isLoading=true` and waits via `whenReady()`
 *  4. Router — guards now see the loading state and wait for hydrate to settle
 *  5. i18n bootstrap — load initial locale before first paint
 *  6. Mount — render only after locale messages are available
 */

import '../style.css';

(async () => {
  const { AUTH_STORAGE_KEYS } = await import('../features/auth/auth-types.js');

  window.addEventListener('pageshow', (event: PageTransitionEvent) => {
    if (event.persisted && !sessionStorage.getItem(AUTH_STORAGE_KEYS.TOKEN)) {
      document.documentElement.style.visibility = 'hidden';
      window.location.reload();
    }
  });

  const [{ createApp }, { default: App }] = await Promise.all([import('vue'), import('./App.vue')]);
  const app = createApp(App);

  const { createPinia } = await import('pinia');
  const pinia = createPinia();
  app.use(pinia);

  const { logger, tryCatch } = await import('../lib/helpers.js');
  const log = logger.createLogger('app:bootstrap');

  const { hydrate } = await import('../features/auth/auth-service.js');
  tryCatch(() => hydrate()).then(([, error]) => {
    if (error) log.error('[FATAL] Hydrate failed unexpectedly', error);
  });

  const { i18n, initI18n } = await import('../features/i18n/i18n-instance.js');
  app.use(i18n);
  await initI18n();

  const { createAppRouter } = await import('./router.js');
  const router = createAppRouter();
  app.use(router);

  app.mount('#app');
})();
